import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { CreditCard, X, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentModal = ({ resumeId, onClose, onSuccess }) => {
  const { authToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [mockPayment, setMockPayment] = useState(false);

  useEffect(() => {
    initiatePayment();
    // eslint-disable-next-line
  }, []);

  const initiatePayment = async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.post(
        `${API}/payments/initiate?resume_id=${resumeId}`,
        {},
        { headers, withCredentials: true }
      );

      if (response.data.can_download) {
        toast.info('You have already paid for this resume');
        onSuccess();
        return;
      }

      setPaymentData(response.data);

      if (!response.data.flutterwave_public_key) {
        setMockPayment(true);
      }
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const handleMockPayment = async () => {
    setLoading(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const mockTransactionId = 'MOCK_' + Date.now();

      await axios.post(
        `${API}/payments/verify?payment_id=${paymentData.payment_id}&transaction_id=${mockTransactionId}`,
        {},
        { headers, withCredentials: true }
      );

      toast.success('Payment simulated successfully!');
      onSuccess();
    } catch (error) {
      toast.error('Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRealPayment = () => {
    if (!paymentData) return;
    setLoading(true);

    if (!window.FlutterwaveCheckout) {
      toast.error('Flutterwave JS SDK not loaded');
      setLoading(false);
      return;
    }

    window.FlutterwaveCheckout({
      public_key: paymentData.flutterwave_public_key,
      tx_ref: paymentData.payment_id,
      amount: paymentData.amount,
      currency: paymentData.currency,
      payment_options: "card, banktransfer, ussd, account, mpesa, mobilemoneyghana, mobilemoneyuganda, mobilemoneyrwanda, mobilemoneyzambia, mobilemoneytanzania, barter, credit, payattitude, nqr",
      customer: {
        email: paymentData.customer_email,
        name: paymentData.customer_name,
      },
      onclose: () => {
        setLoading(false);
      },
      callback: function (response) {
        // Post transaction verification to backend
        verifyFlutterwavePayment(response.transaction_id, paymentData.payment_id);
      },
      customizations: {
        title: "Quick Hire Payment",
        description: "Resume Download",
        logo: "https://quick-hire.org/logo.png"
      },
    });
  };

  const verifyFlutterwavePayment = async (transactionId, paymentId) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.post(
        `${API}/payments/verify?payment_id=${paymentId}&transaction_id=${transactionId}`,
        {},
        { headers, withCredentials: true }
      );
      toast.success('Payment successful!');
      onSuccess();
    } catch (error) {
      toast.error('Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          data-testid="close-payment-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <CreditCard className="h-12 w-12 text-blue-600 mx-auto mb-2" />
          <h2 className="text-2xl font-bold">Download Resume</h2>
          <p className="text-gray-600 mt-2">Complete payment to download your professional resume</p>
        </div>

        {mockPayment && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Demo Mode</p>
              <p>Flutterwave payment gateway is not configured. You can simulate a payment for testing purposes.</p>
            </div>
          </div>
        )}

        {paymentData && (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Amount to Pay</p>
              <p className="text-4xl font-bold text-blue-600">
                $20.00
              </p>
              <p className="text-sm text-gray-500 mt-1">USD</p>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm text-gray-600">Customer Email</Label>
                <p className="font-medium">{paymentData.customer_email}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Customer Name</Label>
                <p className="font-medium">{paymentData.customer_name}</p>
              </div>
            </div>

            <div className="space-y-3">
              {mockPayment ? (
                <Button
                  onClick={handleMockPayment}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
                  disabled={loading}
                  data-testid="mock-payment-btn"
                >
                  {loading ? 'Processing...' : 'Simulate Payment (Test Mode)'}
                </Button>
              ) : (
                <Button
                  onClick={handleRealPayment}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
                  disabled={loading}
                  data-testid="real-payment-btn"
                >
                  {loading ? 'Processing...' : 'Pay with Flutterwave'}
                </Button>
              )}

              <Button
                variant="outline"
                onClick={onClose}
                className="w-full"
                data-testid="cancel-payment-btn"
              >
                Cancel
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Secure payment powered by Flutterwave. Your payment information is encrypted and secure.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentModal;
